import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Lock,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Trash2,
  Timer,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { SwipaSend as Send } from '@/icons/SwipaSend';
import { secretChatsApi, type SecretMessageRow } from '@/api/secretChats';
import { usersApi } from '@/api/users';
import { useAuthStore } from '@/store/authStore';
import { useMobile } from '@/pages/chat/ChatPage';
import {
  ensureIdentityPublished,
  receiveSecretMessages,
  sendSecretMessage,
  destroyLocalSession,
  getCachedSession,
  SecretChatUnsupportedError,
  type DecryptedSecretMessage,
} from '@/lib/secretChat/session';
import { isWebCryptoAvailable } from '@/lib/secretChat/crypto';
import { wipeSecretChatStore } from '@/lib/secretChat/storage';
import SafetyNumberModal from '@/components/chat/SafetyNumberModal';
import Avatar from '@/components/ui/Avatar';
import { extractError } from '@/api/client';

const POLL_INTERVAL_MS = 3000;

const TIMER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Выкл.' },
  { value: 5, label: '5 сек' },
  { value: 30, label: '30 сек' },
  { value: 60, label: '1 мин' },
  { value: 60 * 60, label: '1 час' },
  { value: 24 * 60 * 60, label: '1 день' },
  { value: 7 * 24 * 60 * 60, label: '1 неделя' },
];

function formatTimer(seconds: number): string {
  if (seconds <= 0) return 'выключен';
  const opt = TIMER_OPTIONS.find((o) => o.value === seconds);
  if (opt) return opt.label;
  return `${seconds} сек`;
}

export default function SecretChatPage() {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { setShowSidebar } = useMobile();

  const [supported] = useState(() => isWebCryptoAvailable());
  const [unsupportedReason, setUnsupportedReason] = useState<string | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [messages, setMessages] = useState<DecryptedSecretMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [timerBusy, setTimerBusy] = useState(false);
  const [showDestroy, setShowDestroy] = useState(false);

  const lastRowIdRef = useRef<string | null>(null);
  const pollingRef = useRef<number | null>(null);
  const scrollBottomRef = useRef<HTMLDivElement>(null);

  // Fetch metadata about the chat itself.
  const {
    data: chat,
    refetch: refetchChat,
    isLoading: chatLoading,
    isError: chatError,
  } = useQuery({
    queryKey: ['secret-chat', chatId],
    queryFn: () => secretChatsApi.get(chatId!),
    enabled: !!chatId && supported,
  });

  // Identify the peer (the other side of the chat).
  const peerId = useMemo(() => {
    if (!chat || !user) return null;
    return chat.initiatorId === user.id ? chat.responderId : chat.initiatorId;
  }, [chat, user]);

  const { data: peer } = useQuery({
    queryKey: ['user', peerId],
    queryFn: () => usersApi.getProfile(peerId!),
    enabled: !!peerId,
  });

  // ── Bootstrap local identity on mount ────────────────────────────────────
  useEffect(() => {
    if (!supported || !chatId) return;
    let cancelled = false;
    (async () => {
      try {
        await ensureIdentityPublished();
        if (!cancelled) setBootstrapped(true);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof SecretChatUnsupportedError) {
          setUnsupportedReason(err.message);
        } else {
          setBootstrapError(extractError(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported, chatId]);

  // Reflect the cached "verified" flag so the header icon stays in sync.
  useEffect(() => {
    if (!chatId) return;
    let cancelled = false;
    getCachedSession(chatId).then((s) => {
      if (!cancelled) setVerified(!!s?.verified);
    });
    return () => {
      cancelled = true;
    };
  }, [chatId, showSafety, messages.length]);

  // ── Polling loop: fetch envelopes, decrypt, append ───────────────────────
  const pollOnce = useCallback(async () => {
    if (!chatId || !peerId || !user) return;
    try {
      const rows: SecretMessageRow[] = await secretChatsApi.fetchMessages(
        chatId,
        lastRowIdRef.current ?? undefined,
      );
      if (rows.length === 0) return;
      const decrypted = await receiveSecretMessages(chatId, peerId, rows, user.id);
      lastRowIdRef.current = rows[rows.length - 1].id;
      setMessages((prev) => {
        // Merge by id, preserving local-echo plaintext for our own messages
        // (the server-returned outgoing rows have empty `text`).
        const byId = new Map(prev.map((m) => [m.id, m]));
        for (const m of decrypted) {
          const existing = byId.get(m.id);
          if (existing) {
            byId.set(m.id, {
              ...existing,
              ...m,
              text: m.isOutgoing && !m.text ? existing.text : m.text,
            });
          } else {
            byId.set(m.id, m);
          }
        }
        return Array.from(byId.values()).sort(
          (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
        );
      });
    } catch (err) {
      // Network blip — keep polling silently.
      console.warn('secret-chat poll failed', (err as Error).message);
    }
  }, [chatId, peerId, user]);

  useEffect(() => {
    if (!bootstrapped || !chatId || !peerId || !user) return;
    pollOnce();
    const id = window.setInterval(pollOnce, POLL_INTERVAL_MS);
    pollingRef.current = id;
    return () => {
      if (pollingRef.current) window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [bootstrapped, chatId, peerId, user, pollOnce]);

  // Auto-expire messages locally when their TTL elapses, even before the
  // server cleanup runs. The server will purge the row eventually.
  useEffect(() => {
    if (messages.length === 0) return;
    const now = Date.now();
    const expiringSoon = messages
      .map((m) => (m.expiresAt ? Date.parse(m.expiresAt) - now : Infinity))
      .filter((d) => d > 0 && d < 24 * 60 * 60 * 1000);
    if (expiringSoon.length === 0) return;
    const next = Math.min(...expiringSoon);
    const t = window.setTimeout(() => {
      setMessages((prev) =>
        prev.filter((m) => !m.expiresAt || Date.parse(m.expiresAt) > Date.now()),
      );
    }, next + 50);
    return () => window.clearTimeout(t);
  }, [messages]);

  // Scroll to bottom when new messages land.
  useEffect(() => {
    scrollBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!chatId || !peerId || !user) return;
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setSendError(null);
    // Optimistic local echo so the sender sees their plaintext immediately.
    const optimisticId = `local-${Date.now()}`;
    const echo: DecryptedSecretMessage = {
      id: optimisticId,
      chatId,
      senderId: user.id,
      isOutgoing: true,
      createdAt: new Date().toISOString(),
      text,
      expiresAt: chat?.selfDestructSeconds
        ? new Date(Date.now() + chat.selfDestructSeconds * 1000).toISOString()
        : null,
    };
    setMessages((prev) => [...prev, echo]);
    setDraft('');
    try {
      await sendSecretMessage(chatId, peerId, text);
      await pollOnce();
    } catch (err) {
      setSendError(extractError(err));
      // Roll back the optimistic echo on failure.
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const handleSetTimer = async (seconds: number) => {
    if (!chatId) return;
    setTimerBusy(true);
    try {
      await secretChatsApi.setTimer(chatId, seconds);
      await refetchChat();
    } finally {
      setTimerBusy(false);
      setShowTimer(false);
    }
  };

  const handleDestroy = async () => {
    if (!chatId) return;
    try {
      await secretChatsApi.delete(chatId);
    } catch {
      /* server may have already wiped it */
    }
    await destroyLocalSession(chatId);
    navigate('/');
  };

  // ── Renders ──────────────────────────────────────────────────────────────
  if (!supported || unsupportedReason) {
    return (
      <UnsupportedScreen
        message={unsupportedReason ?? 'WebCrypto / IndexedDB недоступны.'}
        onBack={() => {
          setShowSidebar(true);
          navigate('/');
        }}
      />
    );
  }

  if (chatLoading || !user) {
    return (
      <div className="flex flex-col flex-1 min-w-0">
        <Header onBack={() => { setShowSidebar(true); navigate('/'); }} />
        <div className="flex-1 flex items-center justify-center text-surface-500 text-sm">
          <Loader2 size={16} className="animate-spin mr-2" /> Загрузка...
        </div>
      </div>
    );
  }

  if (chatError || !chat) {
    return (
      <div className="flex flex-col flex-1 min-w-0">
        <Header onBack={() => { setShowSidebar(true); navigate('/'); }} />
        <div className="flex-1 flex items-center justify-center text-center px-4">
          <div>
            <p className="text-surface-400 mb-2">Тунель не найден</p>
            <button
              onClick={() => { setShowSidebar(true); navigate('/'); }}
              className="text-brand-400 text-sm hover:underline"
            >
              Назад
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-w-0 bg-surface-950">
      {/* Header */}
      <div className="h-14 border-b border-brand-900/60 px-3 flex items-center gap-3 flex-shrink-0 bg-gradient-to-r from-surface-900 to-surface-900/70">
        <button
          onClick={() => { setShowSidebar(true); navigate('/'); }}
          className="md:hidden btn-ghost p-1.5 text-surface-400 -ml-1"
        >
          <ArrowLeft size={20} />
        </button>
        {peer && (
          <Avatar src={peer.avatarUrl} username={peer.username} size="sm" status={peer.status} />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-surface-100 text-sm leading-tight truncate flex items-center gap-1.5">
            <Lock size={12} className="text-brand-400 flex-shrink-0" />
            {peer?.displayName ?? 'Тунель'}
          </p>
          <p className="text-xs leading-tight text-brand-400/80 truncate">
            Тунель · {formatTimer(chat.selfDestructSeconds)}
          </p>
        </div>

        <button
          onClick={() => setShowSafety(true)}
          title="Код безопасности"
          className="w-8 h-8 rounded-full bg-surface-800 text-surface-300 hover:bg-surface-700 flex items-center justify-center transition-colors"
        >
          {verified ? (
            <ShieldCheck size={14} className="text-green-400" />
          ) : (
            <ShieldAlert size={14} className="text-yellow-400" />
          )}
        </button>
        <button
          onClick={() => setShowTimer((v) => !v)}
          title="Самоуничтожение"
          className="w-8 h-8 rounded-full bg-surface-800 text-surface-300 hover:bg-surface-700 flex items-center justify-center transition-colors"
        >
          <Timer size={14} />
        </button>
        <button
          onClick={() => setShowDestroy(true)}
          title="Удалить чат"
          className="w-8 h-8 rounded-full bg-surface-800 text-surface-300 hover:bg-red-700 hover:text-white flex items-center justify-center transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Timer popover */}
      {showTimer && (
        <div className="border-b border-surface-800 bg-surface-900 px-4 py-3 flex flex-wrap gap-2">
          {TIMER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              disabled={timerBusy}
              onClick={() => handleSetTimer(opt.value)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                chat.selfDestructSeconds === opt.value
                  ? 'border-brand-500 bg-brand-900/40 text-brand-200'
                  : 'border-surface-700 text-surface-300 hover:border-surface-500'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Trust banner */}
      {!verified && (
        <button
          onClick={() => setShowSafety(true)}
          className="border-b border-yellow-900/40 bg-yellow-900/15 px-4 py-2 text-xs text-yellow-300 flex items-center gap-2 hover:bg-yellow-900/25 transition-colors"
        >
          <Shield size={12} />
          Сессия не подтверждена. Сравните код безопасности с собеседником.
        </button>
      )}

      {/* Bootstrap error */}
      {bootstrapError && (
        <div className="border-b border-red-900/40 bg-red-900/20 px-4 py-2 text-xs text-red-300 flex items-center gap-2">
          <AlertTriangle size={12} /> {bootstrapError}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
        {!bootstrapped && !bootstrapError && (
          <div className="flex items-center justify-center text-surface-500 text-sm py-6">
            <Loader2 size={14} className="animate-spin mr-2" /> Установка ключей...
          </div>
        )}
        {bootstrapped && messages.length === 0 && (
          <div className="text-center text-surface-500 text-sm py-10 px-4">
            <Lock size={28} className="mx-auto mb-2 opacity-40" />
            <p>Защищённая переписка ещё не началась.</p>
            <p className="text-xs mt-1">Первое сообщение установит сессию.</p>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} m={m} />
        ))}
        <div ref={scrollBottomRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-surface-800 bg-surface-900 px-3 py-3 flex items-end gap-2 flex-shrink-0">
        <textarea
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Сообщение шифруется на этом устройстве..."
          disabled={!bootstrapped || sending}
          className="flex-1 resize-none bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 placeholder:text-surface-500 outline-none focus:border-brand-500 disabled:opacity-50 max-h-40"
        />
        <button
          onClick={handleSend}
          disabled={!bootstrapped || sending || !draft.trim()}
          className="btn-primary h-10 w-10 flex items-center justify-center p-0"
          title="Отправить"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
      {sendError && (
        <div className="bg-red-900/40 border-t border-red-700 text-red-300 text-xs px-4 py-2">
          {sendError}
        </div>
      )}

      <SafetyNumberModal
        open={showSafety}
        chatId={chatId!}
        peerName={peer?.displayName ?? 'собеседника'}
        onClose={() => setShowSafety(false)}
      />

      {/* Destroy confirmation */}
      {showDestroy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowDestroy(false)} />
          <div className="relative bg-surface-900 border border-surface-700 rounded-xl w-full max-w-sm p-5">
            <h3 className="text-surface-100 font-semibold mb-2 flex items-center gap-2">
              <Trash2 size={16} className="text-red-400" /> Удалить тунель?
            </h3>
            <p className="text-surface-400 text-sm mb-4">
              Все локальные ключи и сообщения будут безвозвратно стёрты на этом
              устройстве. На сервере конверты также удаляются.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowDestroy(false)} className="btn-ghost flex-1">
                Отмена
              </button>
              <button
                onClick={handleDestroy}
                className="flex-1 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white text-sm font-medium transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function Header({ onBack }: { onBack: () => void }) {
  return (
    <div className="h-14 border-b border-surface-800 px-3 flex items-center gap-3 flex-shrink-0 bg-surface-900">
      <button onClick={onBack} className="md:hidden btn-ghost p-1.5 text-surface-400 -ml-1">
        <ArrowLeft size={20} />
      </button>
      <span className="text-surface-300 text-sm font-medium flex items-center gap-2">
        <Lock size={14} className="text-brand-400" /> Тунель
      </span>
    </div>
  );
}

function MessageBubble({ m }: { m: DecryptedSecretMessage }) {
  const isOut = m.isOutgoing;
  const isError = m.text.startsWith('⚠️');
  return (
    <div className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm whitespace-pre-wrap break-words ${
          isError
            ? 'bg-red-900/40 text-red-300 border border-red-800'
            : isOut
              ? 'bg-brand-700 text-white'
              : 'bg-surface-800 text-surface-100'
        }`}
      >
        {m.text || (isOut ? <span className="opacity-60 italic">отправлено</span> : '...')}
        <div className="text-[10px] opacity-60 mt-0.5 flex items-center gap-1 justify-end">
          {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {m.expiresAt && <Timer size={9} />}
        </div>
      </div>
    </div>
  );
}

function UnsupportedScreen({ message, onBack }: { message: string; onBack: () => void }) {
  const handleWipe = async () => {
    try {
      await wipeSecretChatStore();
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="flex flex-col flex-1 min-w-0">
      <Header onBack={onBack} />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-4">
          <AlertTriangle size={40} className="mx-auto text-yellow-400 opacity-70" />
          <h2 className="text-surface-100 font-semibold">Тунели недоступны</h2>
          <p className="text-surface-400 text-sm leading-relaxed">{message}</p>
          <p className="text-surface-500 text-xs">
            Откройте сайт в Chrome, Firefox, Safari или Edge через защищённое соединение
            (https / localhost). Приватный режим может ограничивать IndexedDB.
          </p>
          <div className="flex gap-2">
            <button onClick={onBack} className="btn-ghost flex-1">Назад</button>
            <button onClick={handleWipe} className="btn-ghost flex-1 text-red-400">
              Стереть локальные ключи
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
